import { useState, useEffect } from 'react'
import { X, Printer, Loader2 } from 'lucide-react'
import api from '../../services/api'
import { formatDate } from '../../utils/helpers'
import { useTranslation } from 'react-i18next'

/**
 * Per-project Invoice document — Finance generates one consolidated bill for the
 * whole project (one line per invoiced milestone). Fetches
 * GET /finance/invoice-document/{projectId} and prints a self-contained,
 * inline-styled document (same pattern as QuotationView / InvoiceView).
 */
export default function InvoiceDocumentView({ projectId, onClose }) {
  const { t } = useTranslation('money')
  const { t: tc } = useTranslation('common')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    api.get(`/finance/invoice-document/${projectId}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Failed to load invoice document'))
      .finally(() => setLoading(false))
  }, [projectId])

  const printDoc = () => {
    if (!data) return
    const rows = data.line_items.map((li, i) => `
      <tr style="border-bottom:1px solid #e5e7eb;">
        <td style="padding:10px 12px;color:#6b7280;font-size:13px;">${i + 1}</td>
        <td style="padding:10px 12px;font-weight:500;font-size:13px;">${li.title}<div style="font-size:10px;color:#9ca3af;font-family:monospace;">${li.invoice_number}</div></td>
        <td style="padding:10px 12px;color:#6b7280;font-size:12px;">${li.status}${li.paid_at ? ` · ${formatDate(li.paid_at)}` : ''}</td>
        <td style="padding:10px 12px;text-align:right;font-size:13px;">$${Number(li.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        <td style="padding:10px 12px;text-align:right;font-weight:600;font-size:13px;">$${Number(li.net_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
      </tr>`).join('')

    const html = `<!DOCTYPE html>
<html><head><meta charset="UTF-8"><title>Invoice ${data.invoice_number}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #111827; }
  @page { margin: 15mm 18mm; size: A4; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head><body>
  <div style="background:#047857;height:6px;width:100%;margin-bottom:28px;border-radius:2px;"></div>
  <table style="width:100%;margin-bottom:24px;"><tr>
    <td style="vertical-align:top;">
      <div style="font-size:20px;font-weight:800;color:#047857;">3 Pillars Data</div>
      <div style="font-size:11px;color:#6b7280;margin-top:4px;">& Training Centre LLC</div>
      <div style="font-size:11px;color:#6b7280;">Sharjah Media City Free Zone, UAE</div>
      <div style="font-size:11px;color:#6b7280;">3pillarsdata@gmail.com</div>
    </td>
    <td style="vertical-align:top;text-align:right;">
      <div style="font-size:30px;font-weight:900;letter-spacing:1px;">${t('project_invoice')}</div>
      <div style="font-size:13px;color:#6b7280;margin-top:6px;">${data.invoice_number}</div>
      <div style="font-size:11px;color:#9ca3af;margin-top:4px;">${formatDate(new Date())}</div>
    </td>
  </tr></table>
  <div style="border-top:2px solid #047857;margin-bottom:22px;"></div>

  <div style="background:#ecfdf5;border:1px solid #a7f3d0;border-radius:10px;padding:18px 22px;margin-bottom:24px;">
    <div style="font-size:9px;text-transform:uppercase;letter-spacing:1.5px;color:#059669;margin-bottom:6px;">${t('project')}</div>
    <div style="font-size:16px;font-weight:700;">${data.project_name || ''}</div>
    ${data.project_number ? `<div style="font-size:11px;color:#6b7280;margin-top:2px;">${data.project_number}${data.external_ref ? ` · Client Ref: ${data.external_ref}` : ''}</div>` : ''}
    ${data.consultant_name ? `<div style="font-size:12px;color:#374151;margin-top:10px;">${t('consultant_label')} <strong>${data.consultant_name}</strong>${data.consultant_code ? ` · ${data.consultant_code}` : ''}${data.consultant_email ? ` · ${data.consultant_email}` : ''}</div>` : ''}
  </div>

  <div style="font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#374151;margin-bottom:10px;">${t('billed_items')}</div>
  <table style="width:100%;border-collapse:collapse;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;">
    <thead><tr style="background:#f9fafb;">
      <th style="padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;color:#9ca3af;border-bottom:1px solid #e5e7eb;">#</th>
      <th style="padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;color:#9ca3af;border-bottom:1px solid #e5e7eb;">${t('milestone_invoice')}</th>
      <th style="padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;color:#9ca3af;border-bottom:1px solid #e5e7eb;">${t('status')}</th>
      <th style="padding:10px 12px;text-align:right;font-size:11px;text-transform:uppercase;color:#9ca3af;border-bottom:1px solid #e5e7eb;">${t('amount')}</th>
      <th style="padding:10px 12px;text-align:right;font-size:11px;text-transform:uppercase;color:#9ca3af;border-bottom:1px solid #e5e7eb;">Net</th>
    </tr></thead>
    <tbody>
      ${rows || `<tr><td colspan="5" style="padding:16px;text-align:center;color:#9ca3af;font-size:13px;">${t('no_invoices_raised')}</td></tr>`}
      <tr style="border-top:2px solid #e5e7eb;"><td colspan="4" style="padding:10px 12px;text-align:right;font-size:13px;color:#374151;">${t('subtotal')}</td><td style="padding:10px 12px;text-align:right;font-weight:600;font-size:13px;">$${Number(data.subtotal).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td></tr>
      ${data.tax_total > 0 ? `<tr><td colspan="4" style="padding:8px 12px;text-align:right;color:#6b7280;font-size:13px;">${t('tax_deductions')}</td><td style="padding:8px 12px;text-align:right;font-size:13px;">-$${Number(data.tax_total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td></tr>` : ''}
      <tr style="background:#ecfdf5;"><td colspan="4" style="padding:12px;text-align:right;font-weight:700;font-size:14px;color:#047857;">NET TOTAL (${data.currency})</td><td style="padding:12px;text-align:right;font-weight:800;font-size:16px;color:#047857;">$${Number(data.net_total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td></tr>
    </tbody>
  </table>

  <div style="border-top:1px solid #e5e7eb;margin-top:24px;padding-top:14px;text-align:center;font-size:11px;color:#9ca3af;">
    3 Pillars Data & Training Centre LLC · ${data.invoice_number} · Generated ${formatDate(new Date())}
  </div>
  <div style="background:#047857;height:4px;width:100%;margin-top:18px;border-radius:2px;"></div>
</body></html>`

    const w = window.open('', '_blank', 'width=900,height=1100')
    w.document.write(html)
    w.document.close()
    setTimeout(() => { w.focus(); w.print() }, 400)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl sticky top-0 z-10">
          <h2 className="font-semibold text-gray-800">{t('project_invoice')}</h2>
          <div className="flex items-center gap-2">
            {data && (
              <button onClick={printDoc} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-lg hover:bg-gray-100">
                <Printer className="w-3.5 h-3.5" /> Print
              </button>
            )}
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="p-6">
          {loading && <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>}
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">{error}</div>}
          {data && (
            <div>
              <div className="flex items-start justify-between pb-4 border-b-2 border-emerald-600 mb-5">
                <div>
                  <div className="text-lg font-bold text-emerald-700">3 Pillars Data & Training LLC</div>
                  <div className="text-xs text-gray-500 mt-0.5">Sharjah Media City Free Zone, UAE</div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-black text-gray-900">{t('project_invoice')}</div>
                  <div className="text-sm text-gray-500">{data.invoice_number}</div>
                </div>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-5">
                <p className="text-[10px] uppercase tracking-widest text-emerald-600 mb-1">{t('project')}</p>
                <p className="font-semibold text-gray-900">{data.project_name}</p>
                {data.project_number && (
                  <p className="text-xs text-gray-500">{data.project_number}{data.external_ref ? ` · Client Ref: ${data.external_ref}` : ''}</p>
                )}
                {data.consultant_name && (
                  <p className="text-xs text-gray-600 mt-2">
                    Consultant: <span className="font-medium">{data.consultant_name}</span>
                    {data.consultant_code && <span className="font-mono text-gray-400"> · {data.consultant_code}</span>}
                  </p>
                )}
              </div>

              <h3 className="text-sm font-semibold text-gray-800 mb-3">{t('billed_items')}</h3>
              <table className="w-full text-sm border-collapse mb-4">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-left px-3 py-2 text-xs text-gray-500 uppercase font-semibold">#</th>
                    <th className="text-left px-3 py-2 text-xs text-gray-500 uppercase font-semibold">{t('milestone')}</th>
                    <th className="text-left px-3 py-2 text-xs text-gray-500 uppercase font-semibold">{tc('labels.status')}</th>
                    <th className="text-right px-3 py-2 text-xs text-gray-500 uppercase font-semibold">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {data.line_items.length === 0 ? (
                    <tr><td colSpan={4} className="px-3 py-4 text-center text-gray-400">{t('no_invoices_raised')}</td></tr>
                  ) : data.line_items.map((li, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="px-3 py-2.5 text-gray-500">{i + 1}</td>
                      <td className="px-3 py-2.5">
                        <p className="font-medium text-gray-900">{li.title}</p>
                        <p className="text-[10px] font-mono text-gray-400">{li.invoice_number}</p>
                      </td>
                      <td className="px-3 py-2.5 text-gray-500">{li.status}</td>
                      <td className="px-3 py-2.5 text-right font-semibold text-gray-900">${Number(li.net_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-gray-200">
                    <td colSpan={3} className="px-3 py-2 text-right text-gray-600">{t('subtotal')}</td>
                    <td className="px-3 py-2 text-right font-semibold">${Number(data.subtotal).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  </tr>
                  {data.tax_total > 0 && (
                    <tr>
                      <td colSpan={3} className="px-3 py-2 text-right text-gray-600">{t('tax_deductions')}</td>
                      <td className="px-3 py-2 text-right">-${Number(data.tax_total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                  )}
                  <tr className="bg-emerald-50">
                    <td colSpan={3} className="px-3 py-3 text-right font-bold text-emerald-700">NET TOTAL ({data.currency})</td>
                    <td className="px-3 py-3 text-right font-extrabold text-emerald-700 text-lg">${Number(data.net_total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
